import * as React from 'react';
import type { ProgrammaticMetrics } from '../../types/programmatic';
import TrendIndicator from './TrendIndicator';

interface ProgrammaticMetricsCardsProps {
  metrics: ProgrammaticMetrics | null;
  isLoading: boolean;
}

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  color: string;
  trend?: number;
}> = ({ title, value, subtitle, icon, color, trend }) => (
  <div className="col-lg col-md-4 col-sm-6 mb-3">
    <div className="card h-100 border-0 shadow-sm">
      <div className="card-body p-3">
        <div className="d-flex align-items-center mb-2">
          <div
            className="rounded p-2 me-2"
            style={{ backgroundColor: `${color}15` }}
          >
            <i className={`fas ${icon}`} style={{ color, fontSize: '1.1rem' }} />
          </div>
          <small className="text-muted fw-semibold">{title}</small>
        </div>
        <h4 className="mb-0 fw-bold">{value}</h4>
        {subtitle && <small className="text-muted">{subtitle}</small>}
        {trend !== undefined && (
          <div className="mt-1">
            <TrendIndicator change={trend} size="sm" />
            <small className="text-muted ms-1">vs prev period</small>
          </div>
        )}
      </div>
    </div>
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="row">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="col-lg col-md-4 col-sm-6 mb-3">
        <div className="card h-100 border-0 shadow-sm">
          <div className="card-body p-3">
            <div className="placeholder-glow">
              <span className="placeholder col-8 mb-2" style={{ height: 14 }} />
              <span className="placeholder col-5" style={{ height: 28 }} />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ProgrammaticMetricsCards: React.FC<ProgrammaticMetricsCardsProps> = ({
  metrics,
  isLoading,
}) => {
  if (isLoading || !metrics) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="row">
      <MetricCard
        title="Native Applicants"
        value={metrics.total_native.toLocaleString()}
        subtitle={`${metrics.native_pct}% of total`}
        icon="fa-user-check"
        color="#198754"
        trend={metrics.trend_change}
      />
      <MetricCard
        title="JS Applicants"
        value={metrics.total_js.toLocaleString()}
        subtitle="Programmatic / paid"
        icon="fa-ad"
        color="#0d6efd"
      />
      <MetricCard
        title="Total Applications"
        value={metrics.total_apps.toLocaleString()}
        subtitle={`${metrics.states_covered} states covered`}
        icon="fa-clipboard-list"
        color="#6f42c1"
      />
      <MetricCard
        title="Pressure Score"
        value={metrics.pressure_score}
        subtitle={
          metrics.pressure_score < 0.5
            ? 'Low - safe to cut spend'
            : metrics.pressure_score <= 2.0
            ? 'Moderate'
            : 'High - keep paid ads'
        }
        icon="fa-tachometer-alt"
        color={
          metrics.pressure_score < 0.5
            ? '#198754'
            : metrics.pressure_score <= 2.0
            ? '#ffc107'
            : '#dc3545'
        }
      />
      <MetricCard
        title="Active Jobs"
        value={metrics.total_jobs.toLocaleString()}
        subtitle={`${metrics.total_profiles.toLocaleString()} profiles`}
        icon="fa-briefcase"
        color="#fd7e14"
      />
    </div>
  );
};

ProgrammaticMetricsCards.displayName = 'ProgrammaticMetricsCards';
export default ProgrammaticMetricsCards;
